import { type RoleSummary } from '../../../APIManager/RoleManager'
import {
  type DataObject,
  type DataObjectList,
  type IPagination
} from '../../../models/DataObject'
import { Actions, FieldType } from '../../../components/CustomTable/types'
import { isViewerCopyRole, VIEWER_COPY_ROLE_PATTERN } from '../../../helpers/roleHelpers'
import { type ViewerRoleRowData } from './types'

const EMPTY_PAGINATION = (totalResults: number): IPagination => ({
  totalResults,
  numPages: 1,
  pageNumber: 1,
  pageSize: Math.max(totalResults, 1)
})

export const isDeletableViewerCopy = (roleName: string): boolean =>
  isViewerCopyRole(roleName)

const toDataObject = (role: RoleSummary): DataObject<ViewerRoleRowData> => ({
  actions: isDeletableViewerCopy(role.roleName) ? [Actions.DELETE] : [],
  data: {
    roleId: role.roleId,
    roleName: role.roleName
  },
  dataPermissions: {
    roleId: [],
    roleName: []
  } as unknown as DataObject<ViewerRoleRowData>['dataPermissions'],
  permissions: [],
  views: []
})

export const sortViewerRoles = (roles: RoleSummary[]): RoleSummary[] => {
  return [...roles].sort((a, b) => {
    if (a.roleName === 'Viewer') return -1
    if (b.roleName === 'Viewer') return 1

    const aMatch = VIEWER_COPY_ROLE_PATTERN.exec(a.roleName)
    const bMatch = VIEWER_COPY_ROLE_PATTERN.exec(b.roleName)
    if (aMatch && bMatch) {
      return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10)
    }

    return a.roleName.localeCompare(b.roleName)
  })
}

export const filterViewerRolesBySearch = (
  roles: RoleSummary[],
  search: string
): RoleSummary[] => {
  const query = search.trim().toLowerCase()
  if (!query) return roles
  return roles.filter((role) => role.roleName.toLowerCase().includes(query))
}

export const buildViewerRolesList = (
  roles: RoleSummary[],
  pagination?: IPagination
): DataObjectList<ViewerRoleRowData> => {
  const sorted = sortViewerRoles(roles)

  return {
    actions: [],
    permissions: [],
    header: {
      field: 'roleName',
      headerName: 'Report Viewer Roles Management',
      alwaysShow: true,
      shown: true,
      type: FieldType.TEXT
    },
    keys: [
      {
        field: 'roleName',
        headerName: 'Role Name',
        alwaysShow: true,
        shown: true,
        type: FieldType.TEXT
      }
    ],
    data: sorted.map(toDataObject),
    pagination: pagination ?? EMPTY_PAGINATION(sorted.length)
  } as unknown as DataObjectList<ViewerRoleRowData>
}

export const paginateViewerRolesList = (
  list: DataObjectList<ViewerRoleRowData>,
  pageNumber: number,
  requestedPageSize: number
): DataObjectList<ViewerRoleRowData> => {
  const totalResults = list.data.length
  const numPages = Math.max(1, Math.ceil(totalResults / requestedPageSize))
  const safePage = Math.min(Math.max(pageNumber, 1), numPages)
  const start = (safePage - 1) * requestedPageSize
  const pageData = list.data.slice(start, start + requestedPageSize)

  // Match server-paginated CDG tables (e.g. RoleTable): `data` is only the
  // current page, while `pagination.page_size` stays the user's requested size.
  return {
    ...list,
    data: pageData,
    pagination: {
      totalResults,
      numPages,
      pageNumber: safePage,
      pageSize: requestedPageSize
    }
  }
}
